import { Grid } from '@mui/material'
import React, { useEffect } from 'react'
import InputAdornment from '@mui/material/InputAdornment'
import { useFormikContext } from 'formik'
import TransformDisplayToEdition from '../../../../components/form/TransformDisplayToEdition'
import { FieldsProps } from '.'
import mapEnumForSelect from '../../../../utils/mapEnumForSelect'
import { displayAsCurrency } from '../../../../utils/displayAsCurrency'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import { DailyBudgetType } from '../../../../__generated__/graphql'
import { FieldGroup, fieldGroupItemSize } from './FieldGroup'
import { useAdgroupBudgetUserPermissions } from '../../../company/userPermissions'
import { isDailyBudgetTypeAllowed } from './adgroupBudgetPermissions.utils'

const DailyBudget: React.FC<FieldsProps> = ({
  adgroup,
  isWizard = false,
  canEdit = true,
  fullWidth = false,
  allowUnchanged = false,
}): JSX.Element => {
  const t = useAppTranslations()
  const permissions = useAdgroupBudgetUserPermissions()
  const {
    values: { dailyBudgetType },
    setFieldValue,
  } = useFormikContext<{ dailyBudgetType: DailyBudgetType | '' }>()

  const dailyBudgetTypeOptionDefs = mapEnumForSelect(DailyBudgetType).map((type) => ({
    value: type.value,
    label: t(`adgroup.budgetAndDates.budgetType.${type.label}`),
  }))

  const allowedDailyBudgetTypeOptions = dailyBudgetTypeOptionDefs.filter((type) =>
    isDailyBudgetTypeAllowed(type.value, permissions)
  )

  const showDailyBudgetTypeSelect = allowUnchanged || allowedDailyBudgetTypeOptions.length > 1
  const fallbackDailyBudgetType = allowedDailyBudgetTypeOptions[0]?.value
  const isCurrentDailyBudgetTypeAllowed =
    dailyBudgetType === '' || isDailyBudgetTypeAllowed(dailyBudgetType, permissions)
  const effectiveDailyBudgetType = isCurrentDailyBudgetTypeAllowed
    ? dailyBudgetType
    : (fallbackDailyBudgetType ?? dailyBudgetType)
  const showDailyBudgetAmount = allowUnchanged
    ? dailyBudgetType === DailyBudgetType.Fixed || dailyBudgetType === ''
    : effectiveDailyBudgetType === DailyBudgetType.Fixed

  useEffect(() => {
    if (allowUnchanged || !fallbackDailyBudgetType) return

    const shouldUseFallback =
      !isDailyBudgetTypeAllowed(dailyBudgetType as DailyBudgetType, permissions) ||
      (!showDailyBudgetTypeSelect && dailyBudgetType !== fallbackDailyBudgetType)

    if (shouldUseFallback) void setFieldValue('dailyBudgetType', fallbackDailyBudgetType)
  }, [
    allowUnchanged,
    dailyBudgetType,
    fallbackDailyBudgetType,
    permissions.adgroupBudgetCapping,
    permissions.adgroupOpenDailyBudget,
    showDailyBudgetTypeSelect,
  ])

  return (
    <FieldGroup size={fullWidth ? 6 : showDailyBudgetTypeSelect ? 4 : 2}>
      {showDailyBudgetTypeSelect && (
        <Grid size={fieldGroupItemSize}>
          <TransformDisplayToEdition
            inputType="select"
            withNone={allowUnchanged}
            noneTranslation="adgroup.budgetAndDates.budgetPlans.unchanged"
            name="dailyBudgetType"
            required={!allowUnchanged}
            label="adgroup.budgetAndDates.budgetTypeLabel"
            tooltipText="adgroup.budgetAndDates.dailyBudgetTypeHelper"
            text={dailyBudgetType ? t(dailyBudgetType) : ''}
            disableHoverEdition={isWizard}
            editable={canEdit}
            options={allowedDailyBudgetTypeOptions}
          />
        </Grid>
      )}
      {showDailyBudgetAmount && (
        <Grid size={fieldGroupItemSize}>
          <TransformDisplayToEdition
            inputType="input"
            type="number"
            name="dailyBudget"
            required={!allowUnchanged}
            label="adgroup.details.dailyBudgetWithCurrency"
            text={displayAsCurrency(adgroup.dailyBudget)}
            tooltipText="adgroup.budgetAndDates.dailyBudgetHelper"
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>,
              inputProps: { min: 15 },
            }}
            disableHoverEdition={isWizard}
            editable={canEdit}
          />
        </Grid>
      )}
    </FieldGroup>
  )
}
export default DailyBudget
